import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { ConsentStatus, getConsentStatus, WindowWithCmp } from './util';

// Extend Window type for Google Consent Mode
declare global {
    interface Window {
        google_tag_data?: {
            ics?: {
                getConsentState: (key: string) => number;
            };
        };
    }
}

const trackConsentState = async (window: WindowWithCmp) => {
    const consentStatus = await getConsentStatus(window);
    const eventName = 'GdprState';
    let eventLabel: string;
    if (consentStatus.consentStatus === ConsentStatus.CONSENTED) {
        eventLabel = `${consentStatus.cmpProvider}-Accepted`;
    } else if (consentStatus.consentStatus === ConsentStatus.DECLINED) {
        eventLabel = `${consentStatus.cmpProvider}-Declined`;
    } else if (consentStatus.consentStatus === ConsentStatus.IMPLICITLY_CONSENTED) {
        eventLabel = `${consentStatus.cmpProvider}-ImplicitlyAccepted`;
    } else {
        eventLabel = `${consentStatus.cmpProvider}-NoInteractionYet`;
    }
    trackV2(eventName, eventLabel, { uaNonInt: true });
};

export default trackConsentState;

// Google consent mode V2 keys
const consentStatusKey: GA4.GoogleConsentStatusKey[] = [
    'analytics_storage',
    'ad_storage',
    'ad_user_data',
    'ad_personalization',
    'functionality_storage',
    'personalization_storage',
    'security_storage',
];

export const setConsentStatusParams = (): GA4.ConsentStatus => {
    // Default all to 'denied' - only 'granted' when user explicitly accepts
    const consentStateParams: GA4.ConsentStatus = {
        analyticsStorage: 'denied',
        adStorage: 'denied',
        adUserData: 'denied',
        adPersonalization: 'denied',
        functionalityStorage: 'denied',
        personalizationStorage: 'denied',
        securityStorage: 'denied',
    };

    const keyMapping: Record<GA4.GoogleConsentStatusKey, keyof GA4.ConsentStatus> = {
        analytics_storage: 'analyticsStorage',
        ad_storage: 'adStorage',
        ad_user_data: 'adUserData',
        ad_personalization: 'adPersonalization',
        functionality_storage: 'functionalityStorage',
        personalization_storage: 'personalizationStorage',
        security_storage: 'securityStorage',
    };

    if (!window.google_tag_data?.ics?.getConsentState) {
        return consentStateParams;
    }

    consentStatusKey.forEach((key) => {
        try {
            const state = window.google_tag_data!.ics!.getConsentState(key);
            const value = state === 1 ? 'granted' : 'denied';
            value === 'granted' && (consentStateParams[keyMapping[key]] = 'granted');
        } catch (_error) {
            // Silently fail for individual keys - keep default 'denied' value
        }
    });

    return consentStateParams;
};

export const waitForConsentUpdate = async (): Promise<void> => {
    return new Promise((resolve) => {
        if (!window.google_tag_data?.ics?.getConsentState) {
            resolve();
            return;
        }

        const getStateSnapshot = () =>
            consentStatusKey.map((key) => {
                try {
                    return window.google_tag_data!.ics!.getConsentState(key);
                } catch {
                    return undefined;
                }
            });

        const startTime = Date.now();
        const maxWaitTime = 3000;
        const pollInterval = 50;
        const stabilityWindow = 400;
        const initialStates = getStateSnapshot();

        let lastStates = [...initialStates];
        let lastChangeTime = Date.now();

        const checkForUpdate = () => {
            const currentStates = getStateSnapshot();
            const stateChanged = currentStates.some((state, index) => state !== lastStates[index]);

            if (stateChanged) {
                lastChangeTime = Date.now();
                lastStates = [...currentStates];
            }

            const hasChangedFromInitial = currentStates.some(
                (state, index) => state !== initialStates[index]
            );

            const timeSinceLastChange = Date.now() - lastChangeTime;
            const isStable = timeSinceLastChange >= stabilityWindow;

            if (hasChangedFromInitial && isStable) {
                resolve();
            } else if (Date.now() - startTime < maxWaitTime) {
                setTimeout(checkForUpdate, pollInterval);
            } else {
                resolve();
            }
        };

        setTimeout(checkForUpdate, pollInterval);
    });
};
